import type { SvgIconComponent } from '@mui/icons-material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import { Button, Typography } from '@mui/material';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { useApi } from '@shared/services/api';
import { useSnackbar } from '@shared/services/SnackbarService/SnackbarServiceContext';
import { useState } from 'react';

interface Feature {
  label: string;
  Icon: SvgIconComponent;
}

const FEATURES: Feature[] = [
  { label: 'Community threads & discussions', Icon: ForumOutlinedIcon },
  { label: 'Unlimited deal review deep dives', Icon: ManageSearchOutlinedIcon },
  { label: 'Request seller consultations', Icon: HandshakeOutlinedIcon },
  { label: 'AI chat assistant', Icon: AutoAwesomeOutlinedIcon },
];

export interface TrialExpiredPaywallProps {
  companyName?: string;
  isContributor?: boolean;
  /**
   * `standalone`: renders the card inline (no frosted glass overlay).
   * Use when there is no content beneath the paywall.
   * Default (undefined / false): absolute-positioned overlay with frosted glass.
   */
  standalone?: boolean;
}

function TrialExpiredPaywall({ companyName, isContributor, standalone }: TrialExpiredPaywallProps) {
  const api = useApi();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const openBillingPortal = async () => {
    setLoading(true);
    try {
      const sep = window.location.search.includes('?') ? '&' : '?';
      const returnUrl = `${window.location.href}${sep}subscriptionRefresh=true`;
      const result = await api.post('subscription/billing-portal', { returnUrl });
      if (result?.url) {
        window.location.href = result.url;
      } else {
        showSnackbar('Could not open billing portal. Please try again.', 'error');
        setLoading(false);
      }
    } catch {
      showSnackbar('Could not open billing portal. Please try again.', 'error');
      setLoading(false);
    }
  };

  const openCheckoutSession = async () => {
    setLoading(true);
    try {
      const result = await api.post('subscription/checkout', {});
      if (result?.url) {
        window.location.href = result.url;
      } else {
        showSnackbar('Could not start checkout. Please try again.', 'error');
        setLoading(false);
      }
    } catch {
      showSnackbar('Could not start checkout. Please try again.', 'error');
      setLoading(false);
    }
  };

  const card = (
    <div className="paywall__card">
      {/* Left — copy + features */}
      <div className="paywall__content-col">
        <div className="paywall__title-row">
          <div className="paywall__icon-badge">
            <LockOutlinedIcon sx={{ fontSize: '1.4rem' }} />
          </div>
          <Typography variant="h4" component="h2" className="paywall__headline">
            {isContributor ? 'Unlock research access' : 'Your free trial has ended'}
          </Typography>
        </div>

        <Typography variant="body1" className="paywall__body-text">
          {isContributor
            ? companyName
              ? `Upgrade to Kandir Pro to access the full ${companyName} profile and research features alongside your contributor perks.`
              : 'Upgrade to Kandir Pro to access research features alongside your contributor perks.'
            : companyName
              ? `Subscribe to unlock the full ${companyName} profile and all Kandir Pro features.`
              : 'Subscribe to unlock the full Kandir Pro experience.'}
        </Typography>

        <div className="paywall__feature-heading">What's included</div>

        <ul className="paywall__feature-list">
          {FEATURES.map(({ label, Icon }) => (
            <li key={label} className="paywall__feature-item">
              <CheckCircleOutlineIcon className="paywall__feature-icon" />
              {label}
            </li>
          ))}
        </ul>
      </div>

      {/* Right — pricing + CTA */}
      <div className="paywall__pricing-col">
        <span className="paywall__price-label">Kandir Pro</span>
        <span className="paywall__price">$49</span>
        <span className="paywall__price-caption">per month · cancel any time</span>

        <Button
          onClick={isContributor ? openCheckoutSession : openBillingPortal}
          variant="contained"
          size="large"
          className="paywall__cta-btn"
        >
          {isContributor ? 'Start 7-day free trial' : 'Subscribe to Kandir Pro'}
        </Button>

        <span className="paywall__stripe-note">Redirected securely to Stripe</span>
      </div>
    </div>
  );

  return (
    <>
      {loading && (
        <LoadingSpinner
          variant="circular"
          size="medium"
          message={isContributor ? 'Starting checkout...' : 'Opening billing portal...'}
          fullScreen
          overlay
        />
      )}

      {standalone ? (
        <div className="paywall paywall--standalone">{card}</div>
      ) : (
        <div className="paywall">
          <div className="paywall__fade" />
          <div className="paywall__glass">{card}</div>
        </div>
      )}
    </>
  );
}

export default TrialExpiredPaywall;
